import dayjs from 'dayjs'
import moment from 'moment'
import React from 'react'
import {
  StatusIndicatorError,
  StatusIndicatorInvisible,
  StatusIndicatorWarning,
  StatusIndicatorInactiveLight,
  StatusIndicatorSuccess,
} from '@app/components/icon'
import { t } from 'i18next'
import i18next from 'i18next'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js'
import { getName } from 'i18n-iso-countries'
import { languages } from '@app/constants/languages'

import { ChartDataResponse, GetMQTTResponse } from '@/api/mqtt.api'
import { getDeviceAlarmsAndErrors } from '@/api/device.api'
import { COLORS } from '@/features/device/components/mocked'
import { TIMEOUT_MS } from '@/constants/devices'
import { RealTimeData } from '@/features/dashboard/components/DeviceCard'
import { ApiError } from '@/api/api-error'
import { DeviceTemplate, ResetTableData } from '@/api/template.api'
import { ActiveDocument } from '@/api/rodo-policies.api'

export enum DeviceStatus {
  NORMAL = 'NORMAL',
  BROKEN = 'BROKEN',
  NOTUSED = 'NOTUSED',
  SCRAPPED = 'SCRAPPED',
}

type UpdateThemeType = (isLight: boolean) => void

type DataItem = {
  type: string
  value: string
  createdAt: string
}

type Data = {
  [key: string]: DataItem | string
}

export type AlertInterval = {
  start: string
  end?: string
  value: string
}

export type ProcessedData = {
  value: number
  createdAt: number
}

export type AlarmInterval = {
  start: number
  end: number
  type: 'alarm' | 'error'
}

export type UsageMonitoringElement = {
  id: string
  partId: string
  partName: string
  name: string
  type: string
  index: number
  warningThreshold: number
  alarmThreshold: number
  usageDirection: 'increasing' | 'decreasing'
  unit: string
  currentValue?: number
  lastUsedDate?: string
  status: 'normal' | 'warning' | 'alarm'
  usagePercentage: number
  estimatedEndDate?: string
  askAboutOfferDate?: string
  installationDate?: string
  dailyAverageUsage?: number
  usageStatistics?: any | null
  minValue?: number
  maxValue?: number
  part?: {
    id: string
    name: string
    category: string
  }
}

export type DevicePartMonitoring = {
  id: string
  installationDate: Date | null
  monitoredElementParamId: string
  askAboutOfferDate?: Date | null
}

export type MonitoredElementParam = {
  id: string
  dataType: string
  index: number
  warningThreshold: number | null
  alarmThreshold: number | null
  unit: string | null
  usageDirection: string
  partId: string
  minValue?: number
  maxValue?: number
  part: {
    id: string
    name: string
    category: string
  }
}

const INACTIVITY_MS = 4000

export const determineDelimiter = (content: string): string => {
  const delimiters = [',', ';', '\t', '|']
  let maxCount = 0
  let chosenDelimiter = delimiters[0]

  delimiters.forEach((delimiter) => {
    const count = content.split(delimiter).length - 1
    if (count > maxCount) {
      maxCount = count
      chosenDelimiter = delimiter
    }
  })

  return chosenDelimiter
}

export const supportedCSVTypes = [
  'text/csv',
  'application/vnd.ms-excel',
  'text/plain',
  'application/csv',
  'application/excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.msexcel',
]

export const validateEmail = (email: string) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return regex.test(email)
}

export const validatePolishMobileNumber = (number: string) => {
  const regex = /^\+48\d{9}$/
  return regex.test(number)
}

export const getStatusIcon = (status: string) => {
  switch (status) {
    case 'error':
      return React.createElement(StatusIndicatorError)
    case 'warning':
      return React.createElement(StatusIndicatorWarning)
    case 'success':
      return React.createElement(StatusIndicatorSuccess)
    case 'inactiveLight':
      return React.createElement(StatusIndicatorInactiveLight)
    case 'invisible':
      return React.createElement(StatusIndicatorInvisible)
    default:
      return React.createElement(StatusIndicatorInvisible)
  }
}

export const getIconForWizardIndicator = (type: string, controlType: string) => {
  if (type === 'RtStatus') return getStatusIcon('success')
  else {
    if (controlType === 'alarm') return getStatusIcon('error')
    else return getStatusIcon('warning')
  }
}

export const getIconForDeviceIndicator = (type: string, props: any, value: any) => {
  if (type === 'RtStatus') {
    const iconStatus = Number(value) ? 'success' : 'inactiveLight'

    return getStatusIcon(iconStatus)
  } else if (props.type === 'alarm') {
    const iconStatus = Number(value) ? 'error' : 'inactiveLight'
    return getStatusIcon(iconStatus)
  } else {
    const iconStatus = Number(value) ? 'warning' : 'inactiveLight'
    return getStatusIcon(iconStatus)
  }
}

export const capitalizeFirstLetter = (word: string): string =>
  `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`

export const truncateString = (string: string, length: number = 12): string =>
  string?.length > 12 ? string.slice(0, length) + '...' : string

export const getWindDirection = (degrees: number) => {
  if (degrees >= 337.5 || degrees < 22.5) return 'Pł.' // Północ
  if (degrees >= 22.5 && degrees < 67.5) return 'Pł-wsch.' // Północny wschód
  if (degrees >= 67.5 && degrees < 112.5) return 'Wsch.' // Wschód
  if (degrees >= 112.5 && degrees < 157.5) return 'Pd-wsch.' // Południowy wschód
  if (degrees >= 157.5 && degrees < 202.5) return 'Pd.' // Południe
  if (degrees >= 202.5 && degrees < 247.5) return 'Pd-zach.' // Południowy zachód
  if (degrees >= 247.5 && degrees < 292.5) return 'Zach.' // Zachód
  if (degrees >= 292.5 && degrees < 337.5) return 'Pł-zach.' // Północny zachód
  else return 'Brak'
}

export const prepareAccordionData = (data, defaultKey, requiredKeys) => {
  const accordionContent = data?.map((item) => {
    const contentData = Object.entries(item)
      .filter(([key]) => requiredKeys.includes(key))
      .map(([key, value]) => (
        <div className='flex gap-2'>
          <div className='font-bold'>{capitalizeFirstLetter(key)}:</div>
          <div>{value as string}</div>
        </div>
      ))

    return { value: item[defaultKey], contentData }
  })

  return accordionContent
}

export const formatDate = (udtTime: string) => {
  const date = new Date(udtTime)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)
  const formattedDate = `${day}.${month}.${year}`

  return formattedDate
}

export const calculateDaysRemaining = (validityDateString: string): number => {
  const validityDate = new Date(validityDateString)
  const today = new Date()

  validityDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  const diffTime = validityDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

export const getTranslationKey = (type: string): string => {
  switch (type) {
    case 'RtStatus':
      return 'forms.wizard.editStatusIndicator'
    case 'AlarmsIndicator':
      return 'forms.wizard.editAlarmsIndicator'
    case 'ProgressBar':
      return 'forms.wizard.editProgressBar'
    case 'AttachmentCard':
      return 'forms.wizard.editAttachmentCard'
    case 'SmallControlCard':
      return 'forms.wizard.editSmallControlCard'
    case 'InfoTabsStatsCard':
      return 'forms.wizard.editInfoCard'
    case 'CraneControlCard':
      return 'forms.wizard.editGantryCraneCard'
    case 'HookControlCard':
      return 'forms.wizard.editGantryCraneCard'
    case 'AdditionalSection':
      return 'forms.wizard.editAdditionalSectionCards'
    case 'GaugeControl':
      return 'forms.wizard.editGaugeControl'
    case 'GaugeCard':
      return 'forms.wizard.editGaugeControl'
    case 'VerticalProgressControl':
      return 'forms.wizard.editVerticalProgressControl'
    default:
      return 'forms.wizard.unknownKey'
  }
}

const to16BitArray = (value: string) => {
  const num = Number(value)

  if (isNaN(num)) return Array(16).fill(0)
  return num.toString(2).padStart(16, '0').split('').map(Number)
}

export const updateTheme: UpdateThemeType = (isLight) => {
  document.documentElement.classList.toggle('dark', !isLight)
  document.documentElement.classList.toggle('light', isLight)

  const theme = isLight ? 'light' : 'dark'
  localStorage.setItem('theme', theme)

  const isMapDetached = localStorage.getItem('isMapDetached') === 'true'
  if (isMapDetached) {
    const bc = new BroadcastChannel('theme-channel')
    bc.postMessage({ type: 'THEME_CHANGE', payload: theme })
    bc.close()
  }
}

export const statusBagdeVariant = (currentDeviceStatus) => {
  switch (currentDeviceStatus) {
    case 'normal':
      return 'success'
    case 'alarm':
      return 'error'
    case 'error':
      return 'warning'
    default:
      return 'darkGrey'
  }
}

export const generalStatusText = (currentDeviceStatus) => {
  switch (currentDeviceStatus) {
    case 'normal':
      return t('device.normal')
    case 'alarm':
      return t('device.alarm')
    case 'error':
      return t('device.error')
    default:
      return t('device.loading')
  }
}
export const getSupportedLanguages = () => {
  return Object.keys(i18next.services.resourceStore.data)
}

export const checkIsOutTimed = (createdAt: string | Date | undefined) => {
  if (!createdAt) return true

  const baseDate = typeof createdAt === 'string' ? new Date(createdAt) : createdAt
  const updatedDate = new Date(baseDate.getTime() + TIMEOUT_MS)
  const now = new Date()

  return updatedDate < now
}

const getMqttValue = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  dataType: string,
  index: number,
  bitIndex?: number,
): number => {
  if (index === null || index < 0) return 0

  const item = mqttData?.find((data) => data?.type === dataType)

  if (!item) return 0

  const values = item.value.split(',')
  const rawValue = values[index]

  if (rawValue === undefined) return 0

  if (bitIndex !== null && bitIndex !== undefined) {
    if (!Number.isInteger(bitIndex) || bitIndex < 0 || bitIndex > 15) return 0
    return to16BitArray(rawValue).reverse()[bitIndex] ?? 0
  }

  return Number(rawValue)
}

const getResetValue = (
  resetTable: ResetTableData,
  dataType: string,
  index: number,
  bitIndex: number | null | undefined,
  fallbackValue: string | number,
): number => {
  const resetData = resetTable?.find((reset) => {
    if (bitIndex !== undefined && bitIndex !== null && reset.bitIndex) {
      return (
        reset.dataType === dataType &&
        Number(reset.index) === Number(index) &&
        Number(bitIndex) === Number(reset.bitIndex)
      )
    }
    return reset.dataType === dataType && Number(reset.index) === Number(index)
  })

  return Number(resetData ? resetData.defaultValue : fallbackValue)
}

export const getCraneControlCardData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: ResetTableData,
) => {
  const {
    arrowLeftDataType,
    arrowLeftIndex,
    arrowRightDataType,
    arrowRightIndex,
    bitIndexLeft,
    bitIndexRight,
    direction,
  } = props

  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)

  const firstDirection = getMqttValue(mqttData, arrowLeftDataType, arrowLeftIndex, bitIndexLeft)
  const secondDirection = getMqttValue(mqttData, arrowRightDataType, arrowRightIndex, bitIndexRight)

  if (isOutTimed) {
    const left = getResetValue(resetTable, arrowLeftDataType, arrowLeftIndex, bitIndexLeft, firstDirection)
    const right = getResetValue(resetTable, arrowRightDataType, arrowRightIndex, bitIndexRight, secondDirection)

    return direction === 'horizontal' ? { left, right } : { up: left, down: right }
  }

  return direction === 'horizontal'
    ? { left: firstDirection, right: secondDirection }
    : { up: firstDirection, down: secondDirection }
}

export const getHookControlCardData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: ResetTableData,
) => {
  const {
    arrowLeftDataType,
    arrowLeftIndex,
    arrowRightDataType,
    arrowRightIndex,
    arrowDownDataType,
    arrowDownIndex,
    arrowUpDataType,
    arrowUpIndex,
    valueDataType,
    valueIndex,
    bitIndexLeft,
    bitIndexRight,
    bitIndexUp,
    bitIndexDown,
    showRotateAndArrows,
  } = props
  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)

  const leftValue = showRotateAndArrows ? getMqttValue(mqttData, arrowLeftDataType, arrowLeftIndex, bitIndexLeft) : 0
  const rightValue = showRotateAndArrows
    ? getMqttValue(mqttData, arrowRightDataType, arrowRightIndex, bitIndexRight)
    : 0
  const upValue = getMqttValue(mqttData, arrowUpDataType, arrowUpIndex, bitIndexUp)
  const downValue = getMqttValue(mqttData, arrowDownDataType, arrowDownIndex, bitIndexDown)
  const valueValue = getMqttValue(mqttData, valueDataType, valueIndex)

  if (isOutTimed) {
    const left = getResetValue(resetTable, arrowLeftDataType, arrowLeftIndex, bitIndexLeft, leftValue)
    const right = getResetValue(resetTable, arrowRightDataType, arrowRightIndex, bitIndexRight, rightValue)
    const up = getResetValue(resetTable, arrowUpDataType, arrowUpIndex, bitIndexUp, upValue)
    const down = getResetValue(resetTable, arrowDownDataType, arrowDownIndex, bitIndexDown, downValue)
    const value = getResetValue(resetTable, valueDataType, valueIndex, null, valueValue)

    return {
      left,
      right,
      up,
      down,
      rotate: right || left,
      value,
    }
  }

  return {
    left: leftValue,
    right: rightValue,
    up: upValue,
    down: downValue,
    rotate: leftValue || rightValue,
    value: valueValue,
  }
}

export const getProgressBarData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
) => {
  const { dataType, index, index2, connector } = props

  if (connector && index2 != null) {
    const value1 = getMqttValue(mqttData, dataType, index)
    const value2 = getMqttValue(mqttData, dataType, index2)

    let formattedValue2 = String(value2)

    formattedValue2 = formattedValue2.length === 1 ? `0${formattedValue2}` : formattedValue2

    return `${value1}${connector}${formattedValue2}`
  }

  return getMqttValue(mqttData, dataType, index)
}

export const getGaugeCardData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: ResetTableData,
) => {
  const { dataType, index, multiplier } = props
  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)
  let value = getMqttValue(mqttData, dataType, index)

  if (isOutTimed) {
    value = getResetValue(resetTable, dataType, index, null, value)
  }

  return multiplier ? value * multiplier : value
}

export const getGaugeControlData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: ResetTableData,
) => {
  const { dataType, index, mathematicalFormula } = props || {}
  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)
  let value = getMqttValue(mqttData, dataType, index)

  if (isOutTimed) {
    value = getResetValue(resetTable, dataType, index, null, value)
  }

  if (mathematicalFormula?.trim()) {
    try {
      const formulaFn = new Function('value', `return ${mathematicalFormula}`)

      const result = formulaFn(value)

      if (!Number.isFinite(result) || isNaN(result)) {
        return 0
      }
      return result
    } catch (error) {
      return 0
    }
  }

  return value
}

export const getRtStatusOrAlarmsIndicatorData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: ResetTableData,
) => {
  const { bitIndex, index, dataType } = props

  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)
  const value = getMqttValue(mqttData, dataType, index, bitIndex)

  if (isOutTimed) return getResetValue(resetTable, dataType, index, bitIndex, value)

  return value
}

export const getSmallControlCardData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: ResetTableData,
) => {
  const {
    operation,
    value: operationValue,
    variant,
    index1,
    index2,
    connector,
    mappedMin,
    mappedMax,
    protocoleMin,
    protocoleMax,
    dataType,
    index,
  } = props

  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)

  if (
    !variant ||
    (variant === 'normal' && (index === null || index === undefined)) ||
    (variant === 'connect' &&
      (index1 === null || index1 === undefined || index2 === null || index2 === undefined || !connector))
  ) {
    return null
  }

  if (variant === 'normal') {
    const value = getMqttValue(mqttData, dataType, index)

    if (isOutTimed) return getResetValue(resetTable, dataType, index, null, value)

    let calculatedValue = value

    if (operationValue !== undefined && operation) {
      if (operation === '/' && operationValue === 0) return value
      calculatedValue = eval(`${calculatedValue}${operation}${operationValue}`)
    }

    if (
      mappedMin !== undefined &&
      mappedMax !== undefined &&
      protocoleMin !== undefined &&
      protocoleMax !== undefined
    ) {
      calculatedValue =
        mappedMin + ((calculatedValue - protocoleMin) / protocoleMax - protocoleMin) * (mappedMax - mappedMin)
    }

    if (isNaN(calculatedValue) || calculatedValue === 0) return value

    return calculatedValue % 1 === 0 ? calculatedValue : calculatedValue.toFixed(2)
  }

  if (
    variant === 'connect' &&
    index1 !== undefined &&
    index1 !== null &&
    index2 !== undefined &&
    index2 !== null &&
    connector
  ) {
    const value1 = getMqttValue(mqttData, dataType, index1)
    const value2 = getMqttValue(mqttData, dataType, index2)

    let formattedValue1 = String(value1)
    let formattedValue2 = String(value2)

    if (connector === ':') {
      formattedValue1 = formattedValue1.length === 1 ? `0${formattedValue1}` : formattedValue1
      formattedValue2 = formattedValue2.length === 1 ? `0${formattedValue2}` : formattedValue2
    }

    return `${formattedValue1}${connector}${formattedValue2}`
  }
}

export const getInfoTabsStatsCardData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: any,
) => {
  const { operation, operationValue, dataType, index } = props

  if (!dataType || index === undefined || index === '') return 0

  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)
  const indexes = index.split(',').map(Number)

  if (isOutTimed && indexes.length === 1) {
    const value = getMqttValue(mqttData, dataType, indexes[0])

    return getResetValue(resetTable, dataType, index, null, value)
  }

  const values = indexes.map((index) => {
    const value = getMqttValue(mqttData, dataType, index)

    if (!operation || operationValue === undefined || (operation === '/' && operationValue === 0)) return value

    return eval(`${value}${operation}${operationValue}`)
  })

  return indexes.length === 1 ? values[0] : values.reduce((acc, next) => acc + next, 0)
}

export const getVerticalProgressControlData = (
  mqttData: GetMQTTResponse[] | Array<{ type: string; value: any; createdAt?: string }>,
  props: any,
  resetTable: ResetTableData,
) => {
  const isOutTimed = checkIsOutTimed(mqttData[0]?.createdAt)

  return props.chartConfigs.map((config) => {
    const { dataType, index, mathematicalFormula } = config

    const value = getMqttValue(mqttData, dataType, index)

    if (isOutTimed) return getResetValue(resetTable, dataType, index, null, value)

    if (mathematicalFormula?.trim()) {
      try {
        const result = new Function('value', `return ${mathematicalFormula}`)(value)
        return Number.isFinite(result) ? result : 0
      } catch {
        return 0
      }
    }

    return value
  })
}

export const getColorRanges = (config: {
  minValue: number
  maxValue: number
  rangeOptions: 'defaultRanges' | 'customRanges'
  colorRanges: { rangeKey: number; color: string }[]
}) => {
  if (config.rangeOptions === 'defaultRanges') {
    const { minValue, maxValue } = config
    return [
      {
        rangeKey: minValue + 0.25 * (maxValue - minValue),
        color: '#FF0000',
      },
      {
        rangeKey: minValue + 0.75 * (maxValue - minValue),
        color: '#FFFF00',
      },
      {
        rangeKey: maxValue,
        color: '#00FF00',
      },
    ]
  }

  return config.colorRanges
}

export const transformData = (data: Data): { type: string; value: string; createdAt: string }[] => {
  const result: { type: string; value: string; createdAt: string }[] = []

  Object.entries(data).forEach(([key, value]) => {
    if (key !== 'deviceId' && typeof value !== 'string') {
      result.push({ ...value })
    }
  })

  return result
}

export const validateDateAndTimeRange = (
  fromDate: Date | string,
  toDate: Date | string,
  fromTime?: string,
  toTime?: string,
): string | null => {
  if (fromTime && toTime) {
    const fromDateTime = dayjs(`${dayjs(fromDate).format('YYYY-MM-DD')}T${fromTime}`)
    const toDateTime = dayjs(`${dayjs(toDate).format('YYYY-MM-DD')}T${toTime}`)
    if (fromDateTime.isAfter(toDateTime)) {
      return 'charts.invalidData'
    }
  } else {
    if (dayjs(fromDate).isAfter(dayjs(toDate))) {
      return 'charts.invalidData'
    }
  }

  return null
}

export const findRealTimeAlert = (realTimeData) => {
  const alert = realTimeData?.find((data) => data.type === 'alert')
  if (alert) {
    return alert.value
  }
  return null
}

export const getDeviceStatus = (realTimeData, historyStatus) => {
  const realtimeAlert = realTimeData?.find(({ type }) => type === 'alert')

  let status

  if (realtimeAlert) status = realtimeAlert.value
  else status = historyStatus

  return status
}

export const checkIsDeviceWorking = (realTimeData) => {
  const realtimeWorkingStatus = realTimeData?.find(({ type }) => type === 'workingStatus')

  if (!realtimeWorkingStatus) return 0

  return realtimeWorkingStatus.value
}

export const getDeviceResurseValue = (realTimeData: { type: string; value: number }[], resource: number): number => {
  const resurs = realTimeData?.find((d) => d.type === 'resurs')

  return resurs ? resurs.value : resource
}

export const getTemplateByLanguage = (
  deviceTemplates: string[],
  availableTemplates: { [key: string]: any },
  selectedLanguage: string,
) => {
  const templatesForDevices = Object.values(availableTemplates).filter(({ id }) => deviceTemplates?.includes(id))
  const templateForSelectedLanguage = templatesForDevices.find(
    (template) => template.language.toUpperCase?.() === selectedLanguage,
  )

  if (templateForSelectedLanguage) return templateForSelectedLanguage
  if (templatesForDevices.length) return templatesForDevices[0]
}

export const getInspectionStatusTextAndColor = (
  latestInspection: { validityDate: Date; warningDays?: number; isChecked?: boolean } | undefined,
  daysUntilExpirationUdt: number | null,
): { text?: string; color?: 'warning' | 'error' } => {
  if (latestInspection && daysUntilExpirationUdt !== null && !latestInspection.isChecked) {
    if (daysUntilExpirationUdt < 0) {
      return {
        text: t('common.inspectionExpired', { daysAgo: Math.abs(daysUntilExpirationUdt) }),
        color: 'error',
      }
    } else if (daysUntilExpirationUdt <= (latestInspection.warningDays ?? 0)) {
      return {
        text: t('common.daysUntilExpirationUdt', { daysUntilExpirationUdt }),
        color: 'warning',
      }
    } else {
      return {
        text: t('common.daysUntilExpirationUdt', { daysUntilExpirationUdt }),
      }
    }
  } else if (latestInspection && daysUntilExpirationUdt !== null) {
    if (daysUntilExpirationUdt < 0) {
      return {
        text: t('common.inspectionExpired', { daysAgo: Math.abs(daysUntilExpirationUdt) }),
      }
    } else {
      return {
        text: t('common.daysUntilExpirationUdt', { daysUntilExpirationUdt }),
      }
    }
  }
  return {}
}

export const fragmentRanges = (
  fragments: [number, number][],
  inactiveRanges: [number, number][],
): [number, number][] => {
  for (const [iStart, iEnd] of inactiveRanges) {
    const newFragments: [number, number][] = []
    for (const [fStart, fEnd] of fragments) {
      if (fEnd <= iStart || fStart >= iEnd) {
        newFragments.push([fStart, fEnd])
      } else {
        if (fStart < iStart) newFragments.push([fStart, Math.max(fStart, iStart)])
        if (fEnd > iEnd) newFragments.push([Math.min(fEnd, iEnd), fEnd])
      }
    }
    fragments = newFragments
  }
  return fragments
}

export const getInactiveRanges = (ds: any[], openedAt?: number) => {
  if (!openedAt || !Array.isArray(ds) || ds.length === 0) return []
  const firstX = ds[0].x
  let ranges = getInactiveRangesFromDataset(ds)
  if (firstX > openedAt) {
    ranges = [[openedAt, firstX], ...ranges]
  }
  return ranges
}

export const getCutRanges = (
  intervals: AlertInterval[],
  type: 'alarm' | 'error',
  firstX: number,
  inactiveRanges: [number, number][],
) => {
  const now = Date.now()
  const cutRanges: [number, number][] = []
  for (const a of intervals) {
    if (type === 'error' && a.value !== 'error') continue
    if (type === 'alarm' && a.value !== 'alarm') continue
    if (!a.start) continue
    let aStart = Math.max(new Date(a.start).getTime(), firstX)
    let aEnd = a.end ? new Date(a.end).getTime() : now
    if (aEnd <= aStart) continue
    const fragments = fragmentRanges([[aStart, aEnd]], inactiveRanges)
    for (const [fStart, fEnd] of fragments) {
      if (fEnd - fStart > 0) cutRanges.push([fStart, fEnd])
    }
  }
  return cutRanges
}

export const getInactiveRangesFromDataset = (dataset: { x: number; y: number | null }[]): [number, number][] => {
  if (!dataset.length) return []
  const result: [number, number][] = []
  let lastActive = dataset[0].x

  for (let i = 1; i < dataset.length; i++) {
    const now = dataset[i].x
    if (dataset[i].y === null) {
      lastActive = now
      continue
    }
    if (now - lastActive > INACTIVITY_MS) {
      result.push([lastActive + 1, now])
    }
    lastActive = now
  }
  const latest = dataset[dataset.length - 1].x
  if (Date.now() - latest > INACTIVITY_MS) {
    result.push([latest + 1, Date.now()])
  }
  return result
}

export const mapToProcessedDataWithRange = (
  item: GetMQTTResponse,
  index: any,
  minValue?: number,
  maxValue?: number,
  usageDirection?: 'increasing' | 'decreasing',
): ProcessedData => {
  const valuesArray = item.value.split(',').map(Number)
  const extractedValue = valuesArray[index]

  let displayValue: number
  if (minValue !== undefined && maxValue !== undefined && maxValue > minValue && usageDirection) {
    displayValue = calculatePercentageFromRange(extractedValue, usageDirection, minValue, maxValue)
  } else {
    displayValue = extractedValue
  }

  const date = new Date(item.createdAt)
  const roundedDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    0,
  )

  return {
    value: displayValue,
    createdAt: roundedDate.getTime(),
  }
}

export const mapToProcessedData = (item: ChartDataResponse, userTimezone?: string): ProcessedData => {
  let finalTimestamp: number

  if (userTimezone) {
    const utcMoment = moment.utc(item.createdAt)
    const localMoment = utcMoment.tz(userTimezone)

    finalTimestamp = localMoment.valueOf()
  } else {
    finalTimestamp = new Date(item.createdAt).getTime()
  }

  return {
    value: item.value,
    createdAt: finalTimestamp,
  }
}

export const mapToAlarmIntervals = (data: any[], processedData: ProcessedData[]): AlarmInterval[] => {
  return data
    .filter((item: any) => item.dataType === 'AL' || item.dataType === 'BP')
    .map((item: any) => {
      const intervalStart = Math.floor(dayjs(item.createdAt).valueOf() / 1000) * 1000
      const intervalEnd = item.resolvedDate ? Math.floor(dayjs(item.resolvedDate).valueOf() / 1000) * 1000 : null
      const firstData = processedData.find((d) => d.createdAt >= intervalStart)

      let lastData
      if (intervalEnd) {
        lastData = [...processedData].reverse().find((d) => d.createdAt <= intervalEnd)
      } else {
        lastData = [...processedData].reverse().find((d) => d.createdAt >= intervalStart)
      }

      if (!firstData || !lastData) return null

      return {
        start: firstData.createdAt,
        end: lastData.createdAt,
        type: item.dataType === 'AL' ? 'alarm' : 'error',
      }
    })
    .filter(Boolean) as AlarmInterval[]
}

export const fetchTodayAlarmsAndErrors = async (deviceId: string) => {
  const timezoneName = dayjs.tz.guess()
  const today = dayjs().format('YYYY-MM-DD')
  const fromTimeUtc = dayjs.tz(`${today}T00:00`, timezoneName).utc().format('HH:mm')
  const toTimeUtc = dayjs.tz(`${today}T23:59`, timezoneName).utc().format('HH:mm')
  const params = {
    fromDate: today,
    toDate: today,
    fromTime: fromTimeUtc,
    toTime: toTimeUtc,
    showAll: false,
    alarms: true,
    errors: true,
    confirmed: true,
    notConfirmed: true,
    active: true,
    notActive: true,
    timezone: timezoneName,
    serialNumber: '',
  }
  const data = await getDeviceAlarmsAndErrors(deviceId, params)
  return data || []
}

export const getCommonChartOptions = (deviceName?: string, title?: string, theme?: string) => ({
  backgroundColor: theme === 'light' ? COLORS['background-light'] : COLORS['background-dark'],

  yAxis: {
    type: 'value',
    axisLabel: {
      color: theme === 'light' ? COLORS['text-dark'] : COLORS['text-light'],
    },
    splitLine: {
      lineStyle: { color: COLORS.grid },
    },
  },
  grid: {
    left: 65,
    right: 150,
    top: 50,
    bottom: 30,
    containLabel: true,
  },
  dataZoom: [
    {
      type: 'inside',
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      preventDefaultMouseMove: true,
    },
  ],
  toolbox: {
    orient: 'vertical',
    itemSize: 35,
    itemGap: 20,
    right: 10,
    showTitle: true,
    feature: {
      restore: { title: t('charts.resetZoom') },
      saveAsImage: {
        title: t('charts.saveAsImage'),
        name: `${deviceName || t('device')}-${title || t('chart')}-${dayjs().format('DD-MM-YYYY')}`,
      },
    },
    iconStyle: { borderColor: COLORS.primary, borderWidth: 2 },
  },
})

export const calculatePercentageFromRange = (
  currentValue: number,
  usageDirection: 'increasing' | 'decreasing',
  minValue?: number,
  maxValue?: number,
): number => {
  if (minValue !== undefined && maxValue !== undefined && maxValue > minValue) {
    const clampedValue = Math.max(minValue, Math.min(maxValue, currentValue))

    if (usageDirection === 'decreasing') {
      return ((maxValue - clampedValue) / (maxValue - minValue)) * 100
    } else {
      return ((clampedValue - minValue) / (maxValue - minValue)) * 100
    }
  } else {
    if (usageDirection === 'decreasing') {
      return Math.max(0, 100 - currentValue)
    } else {
      return Math.max(0, currentValue)
    }
  }
}

export const getCurrentValueFromContext = (element: MonitoredElementParam, deviceMesurementsHistory: any[]): number => {
  if (!deviceMesurementsHistory || !Array.isArray(deviceMesurementsHistory) || deviceMesurementsHistory.length === 0) {
    return 0
  }

  const measurement = deviceMesurementsHistory.find((measurement) => measurement.type === element.dataType)

  if (!measurement) {
    return 0
  }

  const values = measurement.value.split(',')
  if (element.index >= values.length) {
    return 0
  }

  const rawValue = values[element.index]
  const currentValue = rawValue ? parseFloat(rawValue) : 0

  if (isNaN(currentValue)) {
    return 0
  }

  return currentValue
}

export const calculateUsageStatus = (
  currentValue: number,
  warningThreshold: number,
  alarmThreshold: number,
  usageDirection: 'increasing' | 'decreasing',
  minValue?: number,
  maxValue?: number,
): 'normal' | 'warning' | 'alarm' => {
  const usagePercentage = calculatePercentageFromRange(currentValue, usageDirection, minValue, maxValue)

  const isExhausted = usagePercentage >= 100

  if (isExhausted) {
    return 'alarm'
  }

  if (usageDirection === 'decreasing') {
    if (usagePercentage >= alarmThreshold) {
      return 'alarm'
    }
    if (usagePercentage >= warningThreshold) {
      return 'warning'
    }
    return 'normal'
  } else {
    if (usagePercentage >= alarmThreshold) {
      return 'alarm'
    }
    if (usagePercentage >= warningThreshold) {
      return 'warning'
    }
    return 'normal'
  }
}

export const calculateDailyAverageUsage = (
  currentValue: number,
  installationDate: Date | null,
  usageDirection: 'increasing' | 'decreasing',
  minValue?: number,
  maxValue?: number,
): number | undefined => {
  if (!installationDate) {
    return undefined
  }

  const now = new Date()
  const installation = new Date(installationDate)
  const daysSinceInstallation = (now.getTime() - installation.getTime()) / (1000 * 60 * 60 * 24)

  if (daysSinceInstallation <= 0) {
    return undefined
  }

  const usagePercentage = calculatePercentageFromRange(currentValue, usageDirection, minValue, maxValue)

  let dailyUsageRate: number

  if (usageDirection === 'decreasing') {
    dailyUsageRate = usagePercentage / daysSinceInstallation
  } else {
    dailyUsageRate = usagePercentage / daysSinceInstallation
  }

  return dailyUsageRate > 0 ? dailyUsageRate : undefined
}

export const calculateEstimatedEndDate = (
  currentValue: number,
  installationDate: Date | null,
  usageDirection: 'increasing' | 'decreasing',
  minValue?: number,
  maxValue?: number,
): string | undefined => {
  if (!installationDate) {
    return undefined
  }

  const usagePercentage = calculatePercentageFromRange(currentValue, usageDirection, minValue, maxValue)

  const isExhausted = usagePercentage >= 100

  if (isExhausted) return 'exhausted'

  const now = new Date()
  const installation = new Date(installationDate)
  const daysSinceInstallation = (now.getTime() - installation.getTime()) / (1000 * 60 * 60 * 24)

  if (daysSinceInstallation <= 0) {
    return undefined
  }

  let dailyUsageRate: number
  let remainingUsage: number

  if (usageDirection === 'decreasing') {
    const totalUsed = usagePercentage
    dailyUsageRate = totalUsed / daysSinceInstallation
    remainingUsage = 100 - usagePercentage
  } else {
    dailyUsageRate = usagePercentage / daysSinceInstallation
    remainingUsage = 100 - usagePercentage
  }

  if (dailyUsageRate <= 0) {
    return undefined
  }

  const daysToExhaustion = remainingUsage / dailyUsageRate

  if (!isFinite(daysToExhaustion) || daysToExhaustion <= 0) {
    return undefined
  }

  const exhaustionDate = new Date()
  exhaustionDate.setDate(exhaustionDate.getDate() + Math.ceil(daysToExhaustion))
  return exhaustionDate.toISOString()
}

export const calculateDisplayValue = (
  currentValue: number,
  usageDirection: 'increasing' | 'decreasing',
  minValue?: number,
  maxValue?: number,
): number => {
  if (minValue !== undefined && maxValue !== undefined && maxValue > minValue) {
    const percentage = calculatePercentageFromRange(currentValue, usageDirection, minValue, maxValue)
    return Math.max(0, Math.min(100, percentage))
  }

  return currentValue
}

export const calculateUsagePercentage = (
  currentValue: number,
  usageDirection: 'increasing' | 'decreasing',
  hasInstallationDate: boolean,
  minValue?: number,
  maxValue?: number,
): number => {
  if (!hasInstallationDate) {
    return 0
  }

  const percentage = calculatePercentageFromRange(currentValue, usageDirection, minValue, maxValue)
  return Math.max(0, Math.min(100, percentage))
}

export const transformDevicePartMonitoringToUsageElement = (
  monitoring: DevicePartMonitoring,
  element: MonitoredElementParam,
  currentValue: number,
  usagePercentage: number,
  status: 'normal' | 'warning' | 'alarm',
  estimatedEndDate?: string,
): UsageMonitoringElement => {
  const installationDate = monitoring.installationDate ? new Date(monitoring.installationDate) : null
  const dailyAverageUsage = calculateDailyAverageUsage(
    currentValue,
    installationDate,
    element.usageDirection as 'increasing' | 'decreasing',
    element.minValue,
    element.maxValue,
  )

  const displayValue = calculateDisplayValue(
    currentValue,
    element.usageDirection as 'increasing' | 'decreasing',
    element.minValue,
    element.maxValue,
  )

  return {
    id: monitoring.id,
    partId: element.partId || '',
    partName: element.part?.name || 'Unknown Part',
    name: element.part?.name || 'Unknown Part',
    type: element.dataType,
    index: element.index,
    warningThreshold: element.warningThreshold || 0,
    alarmThreshold: element.alarmThreshold || 0,
    usageDirection: element.usageDirection as 'increasing' | 'decreasing',
    unit: element.unit || '%',
    currentValue: Math.round(displayValue * 100) / 100,
    lastUsedDate: new Date().toISOString(),
    status: status,
    usagePercentage: Math.round(usagePercentage * 100) / 100,
    estimatedEndDate: estimatedEndDate,
    askAboutOfferDate: monitoring.askAboutOfferDate ? new Date(monitoring.askAboutOfferDate).toISOString() : undefined,
    installationDate: monitoring.installationDate ? new Date(monitoring.installationDate).toISOString() : undefined,
    dailyAverageUsage: dailyAverageUsage ? Math.round(dailyAverageUsage * 100) / 100 : undefined,
    minValue: element.minValue,
    maxValue: element.maxValue,
    part: element.part
      ? {
          id: element.part.id,
          name: element.part.name,
          category: element.part.category || 'Unknown',
        }
      : undefined,
  }
}

export const processDeviceUsageMonitoringData = (
  devicePartMonitorings: DevicePartMonitoring[],
  monitoredElementParams: MonitoredElementParam[],
  deviceMeasurementsHistory: GetMQTTResponse[],
): UsageMonitoringElement[] => {
  return devicePartMonitorings
    .map((monitoring) => {
      const element = monitoredElementParams.find((param) => param.id === monitoring.monitoredElementParamId)

      if (!element) {
        return null
      }

      const currentValue = getCurrentValueFromContext(element, deviceMeasurementsHistory)

      const usagePercentage = calculateUsagePercentage(
        currentValue,
        element.usageDirection as 'increasing' | 'decreasing',
        !!monitoring.installationDate,
        element.minValue,
        element.maxValue,
      )

      const status = calculateUsageStatus(
        currentValue,
        element.warningThreshold || 0,
        element.alarmThreshold || 0,
        element.usageDirection as 'increasing' | 'decreasing',
        element.minValue,
        element.maxValue,
      )

      const estimatedEndDate = calculateEstimatedEndDate(
        currentValue,
        monitoring.installationDate,
        element.usageDirection as 'increasing' | 'decreasing',
        element.minValue,
        element.maxValue,
      )

      return transformDevicePartMonitoringToUsageElement(
        monitoring,
        element,
        currentValue,
        usagePercentage,
        status,
        estimatedEndDate,
      )
    })
    .filter(Boolean) as UsageMonitoringElement[]
}

export const createHandleChartEvents = (navigate: any, id: string | undefined) => ({
  click: (params: any) => {
    if (params.componentType === 'markArea' && params.data) {
      const meta = Array.isArray(params.data) ? params.data[0]?.meta : params.data.meta
      if (meta && (meta.type === 'alarm' || meta.type === 'error') && id) {
        navigate(`/device/${id}/alarms`)
      }
    }
  },
  mousemove: (params: any) => {
    if (params.componentType === 'markArea' && params.data) {
      const meta = Array.isArray(params.data) ? params.data[0]?.meta : params.data.meta
      const color = Array.isArray(params.data) ? params.data[0]?.itemStyle?.color : params.data.itemStyle?.color

      if (
        meta &&
        (meta.type === 'alarm' || meta.type === 'error') &&
        (color === (COLORS['alarm-dark'] || COLORS['alarm-light']) ||
          color === COLORS['error-dark'] ||
          color === COLORS['error-light'])
      ) {
        createTooltip(params.event.event.clientX, params.event.event.clientY)
        return
      }
    }
  },
})

export const createTooltip = (x: number, y: number) => {
  let tooltip = document.getElementById('alarm-tooltip')
  if (!tooltip) {
    tooltip = document.createElement('div')
    tooltip.id = 'alarm-tooltip'
    tooltip.style.position = 'fixed'
    tooltip.style.background = '#222'
    tooltip.style.color = '#fff'
    tooltip.style.padding = '6px 12px'
    tooltip.style.borderRadius = '6px'
    tooltip.style.fontSize = '14px'
    tooltip.style.pointerEvents = 'none'
    tooltip.style.zIndex = '9999'
    tooltip.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'
    tooltip.style.whiteSpace = 'nowrap'
    tooltip.style.border = `1.5px solid ${COLORS.primary}`
    tooltip.innerText = t('charts.goToAlertPanel')
    document.body.appendChild(tooltip)
  }
  tooltip.style.left = `${x + 12}px`
  tooltip.style.top = `${y - 24}px`
  return tooltip
}

export const getAllDevicesFromProfile = (profile: any): any[] => {
  if (!profile) return []

  const firmDevices = profile.firms.flatMap(({ devices }) => devices)
  const userDevices = profile.devices

  return [...firmDevices, ...userDevices]
}

export const calculateAverageDailyUsage = (dataArr: Array<{ x: Date; y: number }>) => {
  if (dataArr.length < 2) return 0
  const sorted = [...dataArr].sort((a, b) => a.x.getTime() - b.x.getTime())
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  const days = (last.x.getTime() - first.x.getTime()) / (1000 * 60 * 60 * 24)
  const usage = Math.abs(last.y - first.y)
  return days > 0 ? usage / days : 0
}

export const generateCountryPrefixOptions = (language: string) => {
  const countries = getCountries()
  return countries
    .map((countryCode) => {
      const countryName = getName(countryCode, language === 'en' ? 'en' : 'pl') || countryCode
      const callingCode = getCountryCallingCode(countryCode)
      return {
        value: countryCode,
        name: `${countryName} (+${callingCode})`,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}
export const generateNotificationTypeOptions = (t: any) => [
  { value: 'THRESHOLD_EXCEEDED', name: t('notifications.types.thresholdExceeded', 'Przekroczenie wartości') },
  { value: 'ERROR', name: t('notifications.types.error', 'Błąd') },
  { value: 'FAILURE', name: t('notifications.types.failure', 'Awaria') },
]
export const generateConditionOptions = (t: any) => [
  { value: 'GREATER_THAN', name: t('notifications.conditions.greaterThan') },
  { value: 'LESS_THAN', name: t('notifications.conditions.lessThan') },
  { value: 'EQUAL', name: t('notifications.conditions.equal') },
]

export const generateLanguageOptions = (t: any) =>
  languages.map((lang) => ({
    value: lang.name.toUpperCase(),
    name: lang.title,
  }))

export const mapLanguageToBackend = (frontendLang: string): string => {
  return frontendLang.toUpperCase()
}

export const updateChartHistory = (
  realTimeHistoryRef: React.MutableRefObject<Record<string, { value: number | null; receivedAt: string }[]>>,
  realTimeData: any,
  serialNumber: string,
  getValueByTypeAndIndex: (data: any, dataType: string, index: string) => { value: number; date: Date },
) => {
  Object.keys(realTimeHistoryRef.current).forEach((key) => {
    const [dataType, index] = key.split('_')
    if (!dataType || index === undefined) return

    const { value, date } = getValueByTypeAndIndex(realTimeData[serialNumber], dataType, index)
    const dateString = date.toISOString()
    const historyArr = realTimeHistoryRef.current[key]

    if (dateString !== historyArr.at(-1)?.receivedAt) {
      historyArr.push({ value, receivedAt: dateString })
    }
  })
}

const closeIntervalsOfType = (intervals: AlertInterval[], targetValue: string, now: string) => {
  for (let i = intervals.length - 1; i >= 0; i--) {
    const interval = intervals[i]
    if (interval.value === targetValue && !interval.end) {
      interval.end = now
    }
  }
}

const addNewIntervalIfNeeded = (intervals: AlertInterval[], value: string, now: string) => {
  const last = intervals.at(-1)
  if (!last || last.end || last.value !== value) {
    intervals.push({ start: now, value })
  }
}

export const updateAlertIntervals = (
  alertIntervalsRef: React.MutableRefObject<AlertInterval[]>,
  realTimeData: any,
  serialNumber: string,
) => {
  const alertObj = realTimeData[serialNumber]?.find((item) => item.type === 'alert')
  if (!alertObj) return

  const value = alertObj.value
  const now = new Date(alertObj.createdAt ?? Date.now()).toISOString()
  const intervals = alertIntervalsRef.current

  if (value === 'error') {
    closeIntervalsOfType(intervals, 'alarm', now)
    addNewIntervalIfNeeded(intervals, value, now)
  } else if (value === 'alarm') {
    closeIntervalsOfType(intervals, 'error', now)
    addNewIntervalIfNeeded(intervals, value, now)
  } else if (value === 'normal') {
    // Close all open intervals
    for (let i = intervals.length - 1; i >= 0; i--) {
      const interval = intervals[i]
      if (!interval.end) {
        interval.end = now
      }
    }
  }
}

export const getValueByTypeAndIndex = (
  data: RealTimeData,
  dataType: string,
  index: string,
): { value: number; date: Date } => {
  const found = data?.find((item) => item.type === dataType)
  if (!found) return { value: 0, date: new Date(Date.now()) }

  const values = found.value.split(',').map(Number)

  const indexes = index.split(',').map(Number)
  const value = indexes.reduce((sum, idx) => sum + (values[idx] ?? 0), 0)

  return { value, date: new Date(found.createdAt ?? Date.now()) }
}

export const translateDeviceStatus = (statusName: string) => {
  const translatedStatusMap = {
    NORMAL: t('device.normal'),
    BROKEN: t('device.broken'),
    NOTUSED: t('device.notUsed'),
    SCRAPPED: t('device.scrapped'),
  } as Record<string, string>
  return translatedStatusMap[statusName]
}

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'common.genericError'
}

export const openBase64InNewTab = (content: string, type?: string) => {
  if (!content) return

  try {
    const base64 = content.includes(',') ? content.split(',')[1] : content
    const binary = atob(base64)
    const array = new Uint8Array(binary.length)

    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i)

    const blob = new Blob([array], { type: type || 'application/pdf' })
    const blobUrl = URL.createObjectURL(blob)

    window.open(blobUrl, '_blank')
  } catch (err) {
    console.error('openBase64InNewTab failed', err)
    throw err
  }
}

export const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (err) => reject(err)
    reader.readAsDataURL(file)
  })

export const getDownloadFilename = (doc: ActiveDocument): string => {
  if (!doc) return 'document'

  const mime = doc.documentationFile.type
  const format = mime.split('/')[1]

  return `${doc.name}.${format}`
}
